import { RefObject, useEffect, useRef, useState } from 'react';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSIcon } from 'components/common/els';

export interface AnnualAmbulatoryCareSelectProps {
  formField: ChartMetaFormField;
}

interface SelectOption {
  value: string;
  label: string;
}

const toOptions = (content: ContentItem[] | undefined): SelectOption[] =>
  (content ?? []).filter((item) => item.value != null && item.value !== '').map((item) => ({ value: String(item.value), label: item.label ?? String(item.value) }));

export const useDismissOnOutsideClick = (ref: RefObject<HTMLElement>, active: boolean, onDismiss: VoidFunction) => {
  useEffect(() => {
    if (!active) return undefined;
    const handleOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [ref, active, onDismiss]);
};

const AnnualAmbulatoryCareSelect = ({ formField }: AnnualAmbulatoryCareSelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { value, disabled, onChange, onBlur, label } = formField;
  const selectedValue = String(value ?? '');

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  const options = toOptions(formField.chartContent as ContentItem[] | undefined);
  const selectedLabel = options.find((option) => option.value === selectedValue)?.label;

  const handleSelect = (nextValue: string) => {
    if (onChange) onChange(formField, { target: { value: nextValue } });
    setOpen(false);
    if (onBlur) onBlur(formField);
  };

  return (
    <div className="annual-ambulatory-care__select c-els-field" ref={containerRef}>
      <span className="c-els-field__wrap c-els-field__wrap--icon-right">
        <ELSIcon prefix="hmds" name="chevron-down" size="1x1o2" customClass="c-els-field__icon c-els-field__icon--right" />
        <button
          type="button"
          className="c-els-field__input annual-ambulatory-care__select-trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className={selectedLabel ? undefined : 'annual-ambulatory-care__select-placeholder'}>{selectedLabel ?? 'Select'}</span>
        </button>
      </span>
      {open && !disabled && (
        <div className="annual-ambulatory-care__select-panel" role="listbox" aria-label={label}>
          <button type="button" role="option" aria-selected={selectedValue === ''} className="annual-ambulatory-care__select-clear" onClick={() => handleSelect('')}>
            Clear
          </button>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === selectedValue}
              key={option.value}
              className="annual-ambulatory-care__select-option"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

AnnualAmbulatoryCareSelect.displayName = 'AnnualAmbulatoryCareSelect';
export default AnnualAmbulatoryCareSelect;
